<script setup lang="ts">
/**
 * Admin → People: everyone who has signed in, and whether they have watched
 * the onboarding video (ADR 0069), with the platform role DOIT AI gave them
 * (ADR 0072).
 *
 * Built for whoever schedules onboarding follow-up interviews: find the
 * people who have not watched it, or watched only an earlier version, and
 * export the list. The same completions are on the Evidence tab, but there
 * they answer "is the control working", not "who do I call".
 *
 * Admins and viewers both read it; only an admin can give or take the
 * Viewer role (the buttons carry `v-write`, and the backend refuses a
 * viewer anyway).
 */
import { computed, onMounted, ref } from "vue";
import { vWrite } from "../admin-read-only";
import {
  getPeople,
  setRole,
  type PeopleResponse,
  type Person,
  type VideoState,
} from "../api/people";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { EMPTY, fmtAge, fmtDate, fmtDateTime } from "../utils/datetime";
import { saveBlob } from "../utils/download";
import SkeletonRows from "./SkeletonRows.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const data = ref<PeopleResponse | null>(null);
const loading = ref(false);
const error = ref("");
const query = ref("");
const statusFilter = ref<"all" | "not_watched" | "outdated" | "watched">("all");
const videoId = ref("");
const busy = ref("");

type SortKey =
  | "name"
  | "email"
  | "teams"
  | "first"
  | "watched"
  | "version"
  | "last"
  | "role";
const sortKey = ref<SortKey>("name");
const sortAsc = ref(true);

const STATUS_LABEL: Record<string, string> = {
  watched: "Watched",
  outdated: "Earlier version",
  not_watched: "Not watched",
};

async function reload() {
  loading.value = true;
  error.value = "";
  try {
    data.value = await getPeople(props.devUser);
    const vids = data.value.videos;
    if (!vids.some((v) => v.id === videoId.value)) {
      // The general video first: it is the one everyone must watch.
      videoId.value =
        (vids.find((v) => v.applies_to === "*") ?? vids[0])?.id ?? "";
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}
onMounted(reload);

const video = computed(
  () => data.value?.videos.find((v) => v.id === videoId.value) ?? null,
);

const NEVER: VideoState = {
  status: "not_watched",
  version: "",
  completed_at: "",
};

function stateOf(p: Person): VideoState {
  return p.videos[videoId.value] ?? NEVER;
}

function nameOf(p: Person): string {
  return p.display_name || p.email || p.user_oid;
}

const counts = computed(() => {
  const c = { watched: 0, outdated: 0, not_watched: 0 };
  for (const p of data.value?.people ?? []) c[stateOf(p).status] += 1;
  return c;
});

function sortValue(p: Person, k: SortKey): string {
  switch (k) {
    case "name":
      return nameOf(p).toLowerCase();
    case "email":
      return p.email.toLowerCase();
    case "teams":
      return p.teams.join(", ").toLowerCase();
    case "first":
      return p.first_seen_at;
    case "watched":
      return stateOf(p).completed_at;
    case "version":
      return stateOf(p).version;
    case "last":
      return p.last_seen_at;
    case "role":
      return p.roles.join(",");
  }
}

const rows = computed<Person[]>(() => {
  const q = query.value.trim().toLowerCase();
  const out = (data.value?.people ?? []).filter((p) => {
    if (
      statusFilter.value !== "all" &&
      stateOf(p).status !== statusFilter.value
    )
      return false;
    if (!q) return true;
    return [p.display_name, p.email, ...p.teams].some((s) =>
      s.toLowerCase().includes(q),
    );
  });
  const dir = sortAsc.value ? 1 : -1;
  // Empty values sort last whichever way round: "never watched" belongs at
  // the end of a date column, not mixed in at the start.
  return out.sort((a, b) => {
    const va = sortValue(a, sortKey.value);
    const vb = sortValue(b, sortKey.value);
    if (!va !== !vb) return va ? -1 : 1;
    return va.localeCompare(vb) * dir;
  });
});

function sortBy(k: SortKey) {
  if (sortKey.value === k) sortAsc.value = !sortAsc.value;
  else {
    sortKey.value = k;
    sortAsc.value = true;
  }
}

function ariaSort(k: SortKey): "ascending" | "descending" | "none" {
  if (sortKey.value !== k) return "none";
  return sortAsc.value ? "ascending" : "descending";
}

/** A spreadsheet cell, quoted, and never read as a formula — a display
 * name starting with `=` must not run when the file is opened. */
function csvCell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

function exportCsv() {
  const header = [
    "Name",
    "Email",
    "Team",
    "First sign-in",
    "Onboarding status",
    "Video watched at",
    "Version watched",
    "Current version",
    "Last active",
    "Role",
  ];
  const lines = rows.value.map((p) => {
    const s = stateOf(p);
    return [
      p.display_name,
      p.email,
      p.teams.join("; "),
      p.first_seen_at,
      STATUS_LABEL[s.status] ?? s.status,
      s.completed_at,
      s.version,
      video.value?.version ?? "",
      p.last_seen_at,
      p.roles.join("; "),
    ]
      .map((v) => csvCell(v ?? ""))
      .join(",");
  });
  // BOM so Excel reads the file as UTF-8 (æøå in names).
  const body = "﻿" + [header.map(csvCell).join(","), ...lines].join("\r\n");
  const day = new Date().toISOString().slice(0, 10);
  saveBlob(
    new Blob([body], { type: "text/csv;charset=utf-8" }),
    `onboarding-${videoId.value || "people"}-${day}.csv`,
  );
}

async function toggleViewer(p: Person) {
  const has = p.roles.includes("viewer");
  const ok = await confirm({
    title: has
      ? `Remove Viewer from ${nameOf(p)}?`
      : `Make ${nameOf(p)} a Viewer?`,
    message: has
      ? "They lose read access to the admin area on their next click."
      : "They can read the admin area — settings, teams, approvals, usage, audit — but change nothing and see no conversations or documents. Recorded in the audit log.",
    confirmLabel: has ? "Remove" : "Make Viewer",
    danger: has,
  });
  if (!ok) return;
  busy.value = p.user_oid;
  try {
    await setRole(p.user_oid, "viewer", !has, props.devUser);
    toast.success(has ? "Viewer removed" : "Viewer assigned", nameOf(p));
    await reload();
  } catch (e) {
    toast.error(
      "Could not change the role",
      e instanceof Error ? e.message : String(e),
    );
  } finally {
    busy.value = "";
  }
}

const columns: { key: SortKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "teams", label: "Team" },
  { key: "first", label: "First sign-in" },
  { key: "watched", label: "Video watched" },
  { key: "version", label: "Version" },
  { key: "last", label: "Last active" },
  { key: "role", label: "Role" },
];
</script>

<template>
  <div class="admin-people">
    <header class="admin-people__header">
      <div>
        <h2>People</h2>
        <p class="muted small">
          Everyone who has signed in, and whether they have watched the
          onboarding video.
          <template v-if="data && data.mode === 'off'">
            The onboarding gate is switched off, so nobody is being asked to
            watch it.
          </template>
        </p>
      </div>
      <div class="admin-people__actions">
        <button class="btn" :disabled="loading" @click="reload">Refresh</button>
        <button
          class="btn btn--primary"
          :disabled="!rows.length"
          @click="exportCsv"
        >
          Export CSV
        </button>
      </div>
    </header>

    <p v-if="error" class="error" role="alert">{{ error }}</p>

    <div v-if="data" class="filters">
      <label>
        Search
        <input
          v-model="query"
          type="search"
          placeholder="Name, email or team"
        />
      </label>
      <label v-if="data.videos.length > 1">
        Video
        <select v-model="videoId">
          <option v-for="v in data.videos" :key="v.id" :value="v.id">
            {{ v.title }} ({{ v.version }})
          </option>
        </select>
      </label>
      <label>
        Status
        <select v-model="statusFilter">
          <option value="all">All ({{ data.people.length }})</option>
          <option value="not_watched">
            Not watched ({{ counts.not_watched }})
          </option>
          <option value="outdated">
            Earlier version only ({{ counts.outdated }})
          </option>
          <option value="watched">Watched ({{ counts.watched }})</option>
        </select>
      </label>
      <span v-if="video" class="muted small">
        Current version of “{{ video.title }}”: <code>{{ video.version }}</code>
      </span>
      <span v-else class="muted small">No onboarding video is configured.</span>
    </div>

    <SkeletonRows v-if="loading && !data" :rows="8" />
    <div v-else-if="data" class="table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th v-for="c in columns" :key="c.key" :aria-sort="ariaSort(c.key)">
              <button class="sort" @click="sortBy(c.key)">
                {{ c.label }}
                <span v-if="sortKey === c.key" aria-hidden="true">{{
                  sortAsc ? "▲" : "▼"
                }}</span>
              </button>
            </th>
            <th><span class="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in rows" :key="p.user_oid">
            <td>{{ p.display_name || EMPTY }}</td>
            <td>{{ p.email || EMPTY }}</td>
            <td>{{ p.teams.join(", ") || EMPTY }}</td>
            <td :title="fmtDateTime(p.first_seen_at)">
              {{ fmtDate(p.first_seen_at) }}
            </td>
            <td>
              <span :class="['pill', `pill--${stateOf(p).status}`]">
                {{ STATUS_LABEL[stateOf(p).status] }}
              </span>
              <span v-if="stateOf(p).completed_at" class="muted small">
                {{ fmtDateTime(stateOf(p).completed_at) }}
              </span>
            </td>
            <td>
              <code v-if="stateOf(p).version">{{ stateOf(p).version }}</code
              ><span v-else>{{ EMPTY }}</span>
            </td>
            <td :title="fmtDateTime(p.last_seen_at)">
              {{ fmtAge(p.last_seen_at) }}
            </td>
            <td>{{ p.roles.includes("viewer") ? "Viewer" : EMPTY }}</td>
            <td class="actions">
              <button
                v-write
                class="link"
                :disabled="busy === p.user_oid"
                @click="toggleViewer(p)"
              >
                {{
                  p.roles.includes("viewer") ? "Remove Viewer" : "Make Viewer"
                }}
              </button>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td :colspan="columns.length + 1" class="muted">Nobody matches.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.admin-people {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-people__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.admin-people__header h2 {
  margin: 0;
}
.admin-people__actions {
  display: flex;
  gap: var(--space-2);
}
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}
.filters label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.85rem;
}
.table-wrap {
  overflow-x: auto;
}
.sort {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.pill {
  display: inline-block;
  margin-right: 0.4rem;
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  font-size: 0.78rem;
  white-space: nowrap;
}
.pill--watched {
  background: color-mix(
    in srgb,
    var(--color-success, #2e7d32) 15%,
    transparent
  );
}
.pill--outdated {
  background: color-mix(
    in srgb,
    var(--color-warning, #b26a00) 18%,
    transparent
  );
}
.pill--not_watched {
  background: color-mix(in srgb, var(--color-error, #c62828) 14%, transparent);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  white-space: nowrap;
}
.actions {
  white-space: nowrap;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
